import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormInput,
  FormLabel,
  Text,
  toast,
  Tooltip
} from '@sparrowengg/twigs-react';
import CustomPill from 'commons/components/custom-pill';
import React, { FormEvent, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { setOauthApp } from 'redux-store/actions';
import { IRootDispatch, IRootState } from 'commons/types';
import { keyboardKeyConstants } from '../constants';
import { APP_STORE, urlRegex } from 'commons/constants';
import ActionEnergy from 'commons/icons/action-energy';
import { getLocalStorageValue } from 'commons/helper';

const schema = Yup.object().shape({
  clientId: Yup.string().required('Client ID is required'),
  clientSecret: Yup.string().required('Client Secret is required'),
  authorizationUrl: Yup.string()
    .matches(urlRegex, 'Enter a valid authorization URL')
    .required('Authorization URL is required'),
  tokenUrl: Yup.string()
    .matches(urlRegex, 'Enter a valid token URL')
    .required('Token URL is required'),
  scopes: Yup.array().of(Yup.string()).required('Scopes are required'),
  pkce: Yup.boolean().nullable(),
  codeVerifier: Yup.string(),
  codeChallengeMethod: Yup.string()
});

const OAuth = () => {
  const [scopeInput, setScopeInput] = useState('');
  const dispatch = useDispatch<IRootDispatch>();
  const { oauth } = useSelector((state: IRootState) => state.configurations);
  const currentApp = useSelector((state: IRootState) => state.currentApp);
  const appValidation = useSelector((state: IRootState) => state.appValidation);
  const {
    register,
    setValue,
    handleSubmit,
    watch,
    getValues,
    reset,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      clientId: '',
      clientSecret: '',
      authorizationUrl: '',
      tokenUrl: '',
      scopes: [],
      pkce: false,
      codeVerifier: '',
      codeChallengeMethod: ''
    }
  });

  const handleSimulate = () => {
    const appStore = getLocalStorageValue(APP_STORE);
    window.open(
      `${process.env.REACT_APP_SPARROW_APPS_URL}/v2/api/ssdk/oauth?callback=${process.env.REACT_APP_DEVELOPER_PORTAL_URL}code-editor?app=${currentApp.appId}&appVersionId=${currentApp.appVersionId}&store=${appStore}`
    );
  };

  const onSubmit: SubmitHandler<any> = (data) => {
    dispatch(setOauthApp(data));
    toast({
      variant: 'success',
      description: 'OAuth Configured Successfully'
    });
  };

  const handleKeyDown = (event: any) => {
    if (
      event.key === keyboardKeyConstants.ENTER &&
      event.target.tagName === keyboardKeyConstants.INPUT
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (!!oauth) {
      reset(oauth);
    }
  }, [oauth]);

  return (
    <Box
      css={{
        height: 'calc(100vh - 73px)',
        width: '100%',
        background: '#151515',
        label: {
          color: '$white800'
        },
        'input, textarea': {
          background: '$white200 !important',
          color: '$white900',
          border: '$black300 !important',
          boxShadow: 'none !important',
          '&:focus, &:hover, &:active, "&:-internal-autofill-selected"': {
            background: '$white200 !important'
          }
        }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
        <Flex alignItems="center" justifyContent="center">
          <Box css={{ width: 800, marginTop: '$36' }}>
            <Flex alignItems="center" justifyContent="space-between">
              <Flex flexDirection="column" gap="$2">
                <Text size="lg" weight="medium" css={{ color: '$white900' }}>
                  OAuth Configuration
                </Text>
                <Text size="sm" css={{ color: '$white600' }}>
                  Fill the below fields to integrate OAuth in your app
                </Text>
              </Flex>
              <Flex alignItems="center" gap="$4">
                <Tooltip
                  content={
                    !appValidation.isValidated
                      ? 'Save changes to enable simulate.'
                      : ''
                  }
                  size="md"
                >
                  <Button
                    size="lg"
                    css={{
                      background: '$neutra900',
                      color: '$primary400',
                      '&:hover, &:focus, &:focus-within, &:active ': {
                        background: 'rgb(40,40,40) !important'
                      }
                    }}
                    leftIcon={<ActionEnergy color="#4A9CA6" />}
                    disabled={!appValidation.isValidated}
                    onClick={() => handleSimulate()}
                  >
                    Simulate
                  </Button>
                </Tooltip>
                <Button
                  type="submit"
                  size="lg"
                  css={{ background: 'transparent !important' }}
                  variant="outline"
                >
                  Save Oauth
                </Button>
              </Flex>
            </Flex>
            <Flex
              flexDirection="column"
              gap="$6"
              css={{
                marginTop: '$16'
              }}
            >
              <FormInput
                label="Client ID"
                size="lg"
                {...register('clientId')}
                error={errors?.clientId?.message}
                errorBorder={!!errors?.clientId?.message}
              />
              <FormInput
                label="Client Secret"
                size="lg"
                {...register('clientSecret')}
                error={errors?.clientSecret?.message}
                errorBorder={!!errors?.clientSecret?.message}
              />
              <FormInput
                label="Authorization URL"
                size="lg"
                {...register('authorizationUrl')}
                error={errors?.authorizationUrl?.message}
                errorBorder={!!errors?.authorizationUrl?.message}
              />
              <FormInput
                label="Token URL"
                size="lg"
                {...register('tokenUrl')}
                error={errors?.tokenUrl?.message}
                errorBorder={!!errors?.tokenUrl?.message}
              />
            </Flex>
            <Box
              css={{
                marginTop: '$12',
                borderTop: '$borderWidths$xs dashed rgba(255, 255, 255, 0.1)',
                paddingTop: '$6'
              }}
            >
              <Flex flexDirection="column" gap="$4">
                <FormLabel>Scopes</FormLabel>
                <FormInput
                  css={{ width: 400 }}
                  value={scopeInput}
                  errorBorder={!!errors.scopes?.message}
                  error={errors?.scopes?.message}
                  onKeyDown={(e) => {
                    if (
                      e.key === keyboardKeyConstants.ENTER &&
                      !!e.currentTarget.value.trim()
                    ) {
                      const previousScopes = getValues('scopes') ?? [];
                      setValue('scopes', [
                        ...previousScopes,
                        scopeInput
                      ] as any);
                      clearErrors('scopes');
                      setScopeInput('');
                    }
                  }}
                  onChange={(e: FormEvent<HTMLInputElement>) =>
                    setScopeInput(e.currentTarget.value)
                  }
                  placeholder="Type and press enter to add your scope here"
                  size="lg"
                />
                <Flex alignItems="center" gap="$2" wrap="wrap">
                  {watch('scopes')?.length > 0 &&
                    watch('scopes').map((scope) => (
                      <CustomPill
                        label={scope ?? ''}
                        variant="success"
                        radius="md"
                        isClosable
                        onCloseHandler={() =>
                          setValue(
                            'scopes',
                            watch('scopes').filter((s) => s !== scope)
                          )
                        }
                      />
                    ))}
                </Flex>
                <Flex
                  flexDirection="column"
                  gap="$6"
                  css={{
                    marginTop: '$12',
                    button: {
                      background: '$white200 !important'
                    }
                  }}
                >
                  <Checkbox
                    size="md"
                    checked={watch('pkce') as boolean}
                    onChange={(value: any) => setValue('pkce', value)}
                  >
                    <Text size="sm">Proof Key for Code Exchange (PKCE)</Text>
                  </Checkbox>
                  <Flex
                    alignItems="center"
                    gap="$6"
                    css={{ paddingLeft: '$14' }}
                  >
                    <Flex flexDirection="column" gap="$2" css={{ flex: 1 }}>
                      <FormLabel>Code Verifier</FormLabel>
                      <FormInput
                        {...register('codeVerifier')}
                        placeholder="Enter Code Verifier"
                        size="lg"
                      />
                    </Flex>
                    <Flex flexDirection="column" gap="$2" css={{ width: 320 }}>
                      <FormLabel>Code Challenge Method</FormLabel>
                      <FormInput
                        {...register('codeChallengeMethod')}
                        placeholder="Enter Code Challenge Method"
                        size="lg"
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </form>
    </Box>
  );
};

export default OAuth;
